import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { Chip } from "../components/ui/editorial/Chip.js";
import { useCurator } from "../useCurator.js";

interface FeedbackPerson {
  id: string;
  name: string;
  /** Имя в дательном падеже — «Ответить Ане». */
  dative: string;
  possessive: string;
  genitive: string; // «в неё», «в нём»
  options: string[];
}

const DEFAULT_PERSON: FeedbackPerson = {
  id: "04",
  name: "Ани",
  dative: "Ане",
  possessive: "её",
  genitive: "в ней",
  options: ["её цитата", "образ жизни", "любит книги", "как написано", "не знаю ещё"],
};

const PEOPLE: Record<string, FeedbackPerson> = {
  "04": DEFAULT_PERSON,
  "03": {
    id: "03",
    name: "Марины",
    dative: "Марине",
    possessive: "её",
    genitive: "в ней",
    options: [
      "как она говорит",
      "её профессия",
      "вечер не до полуночи",
      "тон письма",
      "не знаю ещё",
    ],
  },
  "02": {
    id: "02",
    name: "Лизы",
    dative: "Лизе",
    possessive: "её",
    genitive: "в ней",
    options: [
      "её фраза о городе",
      "любит архитектуру",
      "спокойный тон",
      "как написано",
      "не знаю ещё",
    ],
  },
  "01": {
    id: "01",
    name: "Кати",
    dative: "Кате",
    possessive: "её",
    genitive: "в ней",
    options: ["её стихи", "как она видит", "любит вечера", "тон письма", "не знаю ещё"],
  },
};

export function Feedback(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const params = useParams<{ id: string }>();
  const id = params.id ?? "04";
  const p = PEOPLE[id] ?? DEFAULT_PERSON;

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [ownOpen, setOwnOpen] = useState(false);
  const [ownText, setOwnText] = useState("");

  const toggle = (opt: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt);
      else next.add(opt);
      return next;
    });
  };

  const canSubmit = useMemo(() => picked.size > 0 || ownText.trim().length >= 2, [picked, ownText]);

  const onSubmit = () => {
    // DEMO: всегда ведём к совпадению через Sent. В проде — только если
    // вторая сторона тоже откликнулась, и не сразу, а асинхронно.
    const nextPath = `/match/${id}`;
    navigate(`/sent?next=${encodeURIComponent(nextPath)}&ms=4500&kind=match`);
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right={`после · ${p.name.toLowerCase()}`} />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[28px]">
        <button
          type="button"
          onClick={() => {
            navigate(`/profile/${id}`);
          }}
          className="self-start editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← назад
        </button>

        <div className="mt-[22px] pr-[18px]">
          <CuratorBubble signature={curator.shortSig}>
            «Прочитали портрет {p.name}. Прежде чем я передам ваш ответ — расскажите, что{" "}
            <em className="italic text-velvet">откликнулось</em>
            ?»
          </CuratorBubble>
        </div>

        <h2 className="mt-[32px] font-display font-light leading-[1.02] tracking-[-0.022em] text-ink text-[40px]">
          Что <em className="italic text-velvet">зацепило</em>
          <br />
          {p.genitive}?
        </h2>

        <div className="mt-[22px] italic font-display font-light text-[13px] text-ink/55">
          выберите одно или несколько — или добавьте своё
        </div>

        <div className="mt-[14px] flex flex-wrap gap-[8px]">
          {p.options.map((opt) => (
            <Chip
              key={opt}
              active={picked.has(opt)}
              onClick={() => {
                toggle(opt);
              }}
            >
              {opt}
            </Chip>
          ))}
          {!ownOpen && (
            <Chip
              variant="own"
              onClick={() => {
                setOwnOpen(true);
              }}
            >
              добавить своё
            </Chip>
          )}
        </div>

        {ownOpen && (
          <div className="mt-[20px] border-t border-ink/12 pt-[12px]">
            <span className="editorial-meta text-ink/55">ваш ответ</span>
            <textarea
              value={ownText}
              onChange={(e) => {
                setOwnText(e.target.value.slice(0, 240));
              }}
              rows={3}
              autoFocus
              placeholder="расскажите своими словами…"
              className="mt-[6px] block w-full resize-none bg-transparent font-display italic font-light text-[17px] leading-[1.4] text-ink outline-none placeholder:text-ink/30 placeholder:not-italic"
            />
            <div className="mt-[4px] editorial-meta text-ink/40 text-right">
              {ownText.length} / 240
            </div>
          </div>
        )}
      </div>

      <div className="px-[22px] pb-[24px] pt-[20px]">
        <PrimaryButton disabled={!canSubmit} onClick={onSubmit}>
          Передать {curator.footerLabel}
        </PrimaryButton>
      </div>
    </Screen>
  );
}
